import React, { useState } from 'react'
import { ArrowRight, ChevronDown, HelpCircle } from 'lucide-react'
import BusinessesImage from '../assets/Delivery.jpg'
import { motion, AnimatePresence } from 'framer-motion'

const faqs = [
  {
    question: 'Why is my package delayed?',
    answer:
      'Delays can be due to high demand, weather, route issues, or internal processing delays. You can check real-time status in tracking.'
  },
  {
    question:
      "What do I do if tracking page doesn't show the expected date of arrival?",
    answer:
      'It may be due to delays in shipment scan updates. You can still track using AWB or contact support for clarity.'
  },
  {
    question: 'How can I get the contact number of my delivery partner?',
    answer:
      'We usually share partner contact via SMS or in the tracking page once out for delivery. Alternatively, contact our support.'
  },
  {
    question: 'What should I do if I am not available at the time of delivery?',
    answer:
      'You can reschedule the delivery via tracking link or inform customer support in advance for coordination.'
  },
  {
    question: 'My shipment has been returned/cancelled. What should I do?',
    answer:
      'Please contact support with your AWB or Order ID for a resolution or re-shipment if eligible.'
  }
]

export default function FAQSection () {
  const [faqOpen, setFaqOpen] = useState(null)

  return (
    <section className='w-full bg-gray-50'>
      <div className='max-w-7xl mx-auto px-6 py-16'>
        <h2 className='text-3xl font-extrabold text-gray-900 mb-10'>
          <span className='border-b-4 border-red-500 pb-1'>FAQs </span>
        </h2>

        {/* Row with FAQs and Yellow Box */}
        <div className='flex flex-col lg:flex-row'>
          {/* FAQ Section - 60% */}
          <div className='flex-[3] bg-[#fff44f] p-6 rounded-l-lg space-y-3'>
            {faqs.map((faq, idx) => {
              const isOpen = faqOpen === idx
              return (
                <div
                  key={idx}
                  className={`rounded-xl border transition-all duration-300 ${
                    isOpen
                      ? 'bg-white border-red-500 shadow-lg'
                      : 'bg-white hover:shadow'
                  }`}
                >
                  <button
                    onClick={() => setFaqOpen(isOpen ? null : idx)}
                    className='flex w-full justify-between items-center px-5 py-5 text-left'
                  >
                    <div className='flex items-start gap-3'>
                      <HelpCircle
                        className={`w-5 h-5 mt-1 transition-colors ${
                          isOpen ? 'text-red-500' : 'text-gray-400'
                        }`}
                      />
                      <span
                        className={`text-base md:text-lg font-semibold transition-colors ${
                          isOpen ? 'text-red-600' : 'text-gray-900'
                        } hover:text-red-500`}
                      >
                        {faq.question}
                      </span>
                    </div>
                    <motion.div
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown className='w-5 h-5 text-gray-400' />
                    </motion.div>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key='answer'
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className='overflow-hidden'
                      >
                        <div className='px-5 pb-5 text-gray-700 text-base leading-relaxed'>
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>

          {/* Right Side – 40% */}
          <div className='flex-[2] bg-[#fff44f] p-6 rounded-r-lg flex flex-col justify-center'>
            <div className='w-full mx-auto text-center'>
              <a
                href='https://sinfiniteexpress.com/get-a-quote/'
                target='_blank'
                rel='noopener noreferrer'
                className='relative block group'
              >
                <img
                  src={BusinessesImage}
                  alt='For Businesses'
                  className='rounded-lg w-full object-cover shadow-md transition-transform duration-300 group-hover:scale-105'
                />
                <span className='absolute bottom-0 right-0 m-2 w-3 h-3 bg-red-500 rounded-sm' />

                <span
                  className='absolute bottom-0 right-0 m-2 w-3 h-3'
                  style={{ backgroundColor: '#211f57' }}
                />
                <p
                  className='text-l mt-3 text-left group-hover:underline font-semibold'
                  style={{ color: '#211f57' }}
                >
                  For Businesses →
                </p>
              </a>
            </div>
          </div>
        </div>

        {/* Need More Help Button */}
        <a
          href='/contact-us'
          className='mt-10 inline-flex px-4 py-2 rounded-md bg-black text-white font-medium text-sm items-center gap-2 hover:bg-gray-800 transition'
        >
          Need More Help? <ArrowRight className='w-4 h-4' />
        </a>
      </div>
    </section>
  )
}
